import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  picture?: string;
  role: 'admin' | 'user' | 'viewer';
  provider: string;
  permissions?: string[]; // Add permissions array
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (provider: string) => void;
  logout: () => void;
  handleOAuthRedirect: (token: string) => void;
  hasPermission: (permission: string) => boolean;
  hasAdminAccess: () => boolean;
  hasUserAccess: () => boolean;
  getDefaultPortal: () => 'admin' | 'user';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for JWT token on app load
    const token = localStorage.getItem('jwt');
    if (token) {
      try {
        // Decode JWT to check expiration
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp && payload.exp < currentTime) {
          // Token has expired
          console.log('JWT token has expired');
          localStorage.removeItem('jwt');
          localStorage.removeItem('authUser');
          setUser(null);
        } else {
          // Token is valid, set user
          const userData = localStorage.getItem('authUser');
          if (userData) {
            try {
              setUser(JSON.parse(userData));
            } catch (error) {
              console.error('Failed to parse user data:', error);
              localStorage.removeItem('jwt');
              localStorage.removeItem('authUser');
            }
          }
        }
      } catch (error) {
        console.error('Failed to decode JWT:', error);
        localStorage.removeItem('jwt');
        localStorage.removeItem('authUser');
      }
    }
    setLoading(false);
  }, []);

  const login = (provider: string) => {
    // Redirect to backend OAuth endpoint
    if (provider === 'email') {
      // Handle email login (you can implement this separately)
      console.log('Email login not implemented yet');
      return;
    }
    
    // Redirect to backend OAuth endpoint through proxy
    //window.location.href = `http://localhost:8080/oauth2/authorization/${provider}`;
    const oauthBaseUrl = process.env.REACT_APP_OAUTH_BASE_URL || '';
    window.location.href = `${oauthBaseUrl}/oauth2/authorization/${provider}`;

  };

  const handleOAuthRedirect = (token: string) => {
    // Store JWT token
    localStorage.setItem('jwt', token);
    console.log('token', token);
    // Decode JWT to get user info (you might want to verify with backend)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userData: User = {
        id: payload.sub || payload.email,
        name: payload.name || payload.email,
        email: payload.email,
        picture: payload.picture,
        role: payload.role || 'user',
        provider: payload.provider || 'oauth',
        permissions: payload.permissions || [], // Extract permissions from JWT
      };
      
      setUser(userData);
      localStorage.setItem('authUser', JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      localStorage.removeItem('jwt');
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('jwt');
    localStorage.removeItem('authUser');
  };

  // Helper functions for permission checking
  const hasPermission = (permission: string): boolean => {
    return user?.permissions?.includes(permission) || false;
  };

  const hasAdminAccess = (): boolean => {
    return hasPermission('ADMIN_ACCESS');
  };

  const hasUserAccess = (): boolean => {
    return hasPermission('USER_ACCESS');
  };

  const getDefaultPortal = (): 'admin' | 'user' => {
    if (hasAdminAccess()) {
      return 'admin'; // Default to admin if they have admin access
    }
    if (hasUserAccess()) {
      return 'user';
    }
    return 'user'; // Fallback to user portal
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    handleOAuthRedirect,
    hasPermission,
    hasAdminAccess,
    hasUserAccess,
    getDefaultPortal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 